import React, { useEffect, useMemo, useRef } from "react";

import { Box, CardActionArea } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { normalizeExternalLink, useAds } from "../../hooks/ad-hook";
import AdCardPreview from "./AdCardPreview";

/**
 * Drop-in ad slot. Give it the placement identifier for the page:
 *
 *   <AdPlacement placement="home" />
 *   <AdPlacement placement="jobs" />
 *   <AdPlacement placement="content" />
 *   <AdPlacement placement="income-directory" />
 *   <AdPlacement placement="teacher-dashboard" />
 *
 * Pulls approved ads for that placement within their run window.
 * When no ad is live, renders `fallback` if provided, otherwise nothing.
 */
const AdPlacement = ({ placement, sx = {}, fallback = null }) => {
  const { getActiveAds, trackAdClick, trackAdImpressions } = useAds();
  const impressionSent = useRef(false);

  const { data: ads = [] } = useQuery({
    queryKey: ["active-ads", placement],
    queryFn: () => getActiveAds(placement),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(placement),
  });

  const ad = useMemo(() => {
    if (!ads?.length) return null;
    return ads[Math.floor(Math.random() * ads.length)];
  }, [ads]);

  useEffect(() => {
    const adId = ad?._id || ad?.id;
    if (adId && !impressionSent.current) {
      impressionSent.current = true;
      trackAdImpressions([adId]);
    }
  }, [ad, trackAdImpressions]);

  if (!ad) return fallback;

  const href = normalizeExternalLink(ad?.externalLink);

  return (
    <Box
      sx={[
        {
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          position: "relative",
        },
        (theme) => ({
          [theme.breakpoints.down("md")]: {
            margin: "0.5rem 0.5rem 0",
          },
          [theme.breakpoints.down("sm")]: {
            margin: "0.5rem 1rem 0",
          },
        }),
        sx,
      ]}
    >
      <AdCardPreview
        image={ad?.image}
        mainTitle={ad?.mainTitle}
        subText={ad?.subText}
        externalLink={ad?.externalLink}
      >
        <CardActionArea
          component="a"
          href={href || undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackAdClick(ad?._id || ad?.id)}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            borderRadius: "6px",
          }}
          aria-label={ad?.mainTitle || "Ad"}
        />
      </AdCardPreview>
    </Box>
  );
};

export default AdPlacement;
